import {useService} from "../../../infrastructure/service/service";
import React, {useEffect, useState} from "react";
import {TitleSection, TitleSectionTemplate} from "../../../entity/model/title-section-template";
import {observer} from "mobx-react-lite";
import {ManageMode} from "../../../entity/enums/manage-mode";
import {User} from "../../../entity/model/user";
import LoadingComponent from "../components/widget/LoadingComponent";
import Box from "@mui/material/Box";
import {
    Button,
    Chip,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import {UserRole} from "../../../entity/enums/user-role";
import TableSortLabel from "@mui/material/TableSortLabel";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TablePagination from "../components/widget/TablePagination";
import ConfirmDialog from "../components/widget/ConfirmDialog";
import SectionTemplateDialog from "../components/section-template/SectionTemplateDialog";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {router} from "../../router/Routes";
import RefreshIcon from "@mui/icons-material/Refresh";
import {Form, Formik, FormikProps} from "formik";
import {SearchInputDto} from "../../../entity/dto/search-input-dto";

import '../../../assets/css/style.css';

const SectionTemplates = (): JSX.Element => {
    const {userService, titleSectionTemplateService, searchService} = useService();

    const [writePermission, setWritePermission] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [refresh, setRefresh] = useState<boolean>(false);
    const [templates, setTemplates] = useState<TitleSectionTemplate[]>([]);
    const [searchTemplates, setSearchTemplates] = useState<TitleSectionTemplate[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<TitleSectionTemplate>();
    const [openSectionTemplateDialog, setOpenSectionTemplateDialog] = useState<boolean>(false);
    const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
    const [mode, setMode] = useState<ManageMode>(ManageMode.CREATE);
    const [loadingConfirmBtn, setLoadingConfirmBtn] = useState<boolean>(false);
    const [currentUser, setCurrentUser] = useState<User>();
    const [menuElement, setMenuElement] = useState<null | HTMLElement>(null);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);

    const pageSize = 100;
    const tableHeaders = ['Name', 'Sections'];

    useEffect(() => {
        const init = async (): Promise<void> => {
            const user = await userService.getCurrentUser();
            setWritePermission(!!(user && user.roles.filter((role: string): boolean => role === UserRole.ADMIN || role === UserRole.ORG_ADMIN).length > 0));
            setCurrentUser(user);

            const sectionTemplates = await titleSectionTemplateService.getTitleSectionTemplates(user?.organizationID ?? '', false);
            setTemplates(sectionTemplates);
            setSearchTemplates(sectionTemplates.slice(0, pageSize));
            setTotalPages(Math.ceil(sectionTemplates.length / pageSize));
        }

        setLoading(true);
        init().then(() => setLoading(false));
    }, [titleSectionTemplateService.getCache()]);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, template: TitleSectionTemplate): void => {
        setMenuElement(event.currentTarget);
        setSelectedTemplate(template);
    }

    const handleMenuClose = (): void => {
        setMenuElement(null);
    }

    const handleSearchChange = (handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void) => (event: React.ChangeEvent<HTMLInputElement>): void => {
        handleChange(event);

        const input = event.target.value.trim().toLowerCase();
        if (!input) {
            setCurrentPage(1);
            setTotalPages(Math.ceil(templates.length / pageSize));
            setSearchTemplates(templates.slice(0, pageSize));
            return;
        }

        const filterTemplates = titleSectionTemplateService.filterTemplates(input);
        setCurrentPage(1);
        setTotalPages(Math.ceil(filterTemplates.length / pageSize));
        setSearchTemplates(filterTemplates.slice(0, pageSize));
    }

    const handlePaginationChange = (event: React.ChangeEvent<unknown>, activePage: number): void => {
        setCurrentPage(activePage);

        const startIndex = (activePage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        setSearchTemplates(templates.slice(startIndex, endIndex));
    }

    const handleOpenSectionTemplateDialog = (mode: ManageMode): void => {
        if (mode === ManageMode.UPDATE)
            handleMenuClose();

        setMode(mode);
        setOpenSectionTemplateDialog(true);
    }

    const handleDeleteTemplate = (): void => {
        handleMenuClose();
        setOpenConfirmDialog(true);
    }

    const deleteTemplate = async (): Promise<void> => {
        setLoadingConfirmBtn(true);
        await titleSectionTemplateService.deleteTitleSectionTemplate(currentUser?.organizationID ?? '', selectedTemplate?.id ?? '');

        setLoadingConfirmBtn(false);
        setOpenConfirmDialog(false);
    }

    const refreshPage = async (): Promise<void> => {
        setRefresh(true);

        const sectionTemplates = await titleSectionTemplateService.getTitleSectionTemplates(currentUser?.organizationID ?? '', true);
        setTemplates(sectionTemplates);
        setSearchTemplates(sectionTemplates.slice(0, pageSize));

        setCurrentPage(1);
        setTotalPages(Math.ceil(sectionTemplates.length / pageSize));

        setRefresh(false);
    }

    const renderTableRow = (template: TitleSectionTemplate, index: number): JSX.Element => {
        return (
            <TableRow
                hover
                tabIndex={-1}
                key={template.id}
                className={'table-cell'}>
                {
                    writePermission &&
                        <TableCell className={'no-wrap'} padding='checkbox'>
                            <IconButton
                                id={`btn-section-template-menu-${index}`}
                                className={'zitles-menu-trigger'}
                                color= "inherit"
                                disableRipple
                                disableFocusRipple
                                onClick={(event: React.MouseEvent<HTMLElement>): void => handleMenuOpen(event, template)}>
                                <MoreHorizIcon />
                            </IconButton>
                            <Menu elevation={0} anchorEl={menuElement} open={Boolean(menuElement)} className={'zitles-menu'} onClose={handleMenuClose}>
                                <MenuItem
                                    id={`menu-section-template-edit-${index}`}
                                    disableRipple
                                    className={'zitles-menu-item'}
                                    onClick={(): void => handleOpenSectionTemplateDialog(ManageMode.UPDATE)}>
                                    <EditIcon className={'zitles-icon'} fontSize={'small'} sx={{marginLeft: '5px'}}/>
                                    <Typography className={'zitles-menu-item button'}>Edit</Typography>
                                </MenuItem>
                                <MenuItem
                                    id={`menu-section-template-delete-${index}`}
                                    disableRipple
                                    className={'zitles-menu-item'}
                                    onClick={handleDeleteTemplate}>
                                    <DeleteIcon className={'zitles-icon'}/>
                                    <Typography className={'zitles-menu-item button'}>Delete</Typography>
                                </MenuItem>
                            </Menu>
                        </TableCell>
                }
                <TableCell id={`text-section-template-name-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{template.name}</TableCell>
                <TableCell id={`text-section-template-sections-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
                    {
                        template.sections
                            .map((titleSection: TitleSection): JSX.Element =>
                                <Chip
                                    className={'zitles-chip'}
                                    variant='outlined'
                                    label={titleSection.name} />)
                    }
                </TableCell>
            </TableRow>
        );
    }

    return (
        <>
            {
                loading
                    ? <LoadingComponent/>
                    : <Box className={'body-box'}>
                        {
                            refresh &&
                                <LoadingComponent/>
                        }
                        <Box display={'flex'} marginBottom={2}>
                            <ArrowBackIcon id={'btn-section-template-back'} className={'breadcrumb-icon'} onClick={(): Promise<void> => router.navigate('/dashboard')}/>
                            <Typography className={'breadcrumb-title'}>Section Templates</Typography>
                        </Box>
                        <Paper className={'table-container'} elevation={0}>
                            <Box className={'toolbar-container'}>
                                {
                                    writePermission &&
                                        <Button
                                            id={'btn-section-template-create'}
                                            className={'zitles-button'}
                                            variant='contained'
                                            startIcon={<AddIcon />}
                                            disabled={!writePermission}
                                            onClick={(): void => handleOpenSectionTemplateDialog(ManageMode.CREATE)}>
                                            New Template
                                        </Button>
                                }
                                <Formik initialValues={searchService.getSearchInputDto()} onSubmit={(): void => {}}>
                                    {({values, handleChange}: FormikProps<SearchInputDto>): JSX.Element => (
                                        <Form>
                                            <TextField
                                                id={'search-section-template'}
                                                name='searchInput'
                                                className={writePermission ? 'zitles-searchbar' : 'zitles-searchbar no-padding'}
                                                variant={'outlined'}
                                                placeholder="Search"
                                                onChange={handleSearchChange(handleChange)}
                                                value={values.searchInput}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position='start'>
                                                            <SearchIcon className={'zitles-searchbar icon'}/>
                                                        </InputAdornment>)
                                                }}/>
                                        </Form>
                                    )}
                                </Formik>
                                <Button
                                    id={'btn-section-template-refresh'}
                                    className={'zitles-button zitles-table refresh'}
                                    variant='contained'
                                    startIcon={<RefreshIcon />}
                                    disabled={refresh}
                                    onClick={refreshPage}>
                                    Refresh
                                </Button>
                            </Box>
                            {
                                searchTemplates.length > 0
                                    ? <TableContainer className={'zitles-table'} component={Paper}>
                                        <Table>
                                            <TableHead>
                                                <TableRow className={'table-header'}>
                                                    {
                                                        writePermission &&
                                                            <TableCell className={'no-wrap'} padding='checkbox'/>
                                                    }
                                                    {
                                                        tableHeaders.map((header: string, index: number): JSX.Element => (
                                                            <TableCell key={index} className={'no-wrap'} align='left' padding='normal'>
                                                                <TableSortLabel
                                                                    id={`section-template-column-${header}`}
                                                                    className={'table-header-text'}
                                                                    active={true}
                                                                    direction='desc'>
                                                                    {header}
                                                                </TableSortLabel>
                                                            </TableCell>
                                                        ))
                                                    }
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {
                                                    searchTemplates.map(renderTableRow)
                                                }
                                            </TableBody>
                                        </Table>
                                      </TableContainer>
                                    : <Typography id={'text-section-template-empty'} className='empty-message'>No section templates found !!</Typography>

                            }
                        </Paper>
                        {
                            searchTemplates.length > 0 &&
                                <TablePagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onChange={handlePaginationChange}/>
                        }
                      </Box>
            }
            {
                openSectionTemplateDialog &&
                    <SectionTemplateDialog
                        open={openSectionTemplateDialog}
                        setOpen={setOpenSectionTemplateDialog}
                        mode={mode}
                        titleSectionTemplate={selectedTemplate}/>
            }
            {
                openConfirmDialog &&
                    <ConfirmDialog
                        open={openConfirmDialog}
                        setOpen={setOpenConfirmDialog}
                        title={'Delete Template'}
                        message={`Are you sure you want to delete ${selectedTemplate?.name}?`}
                        handleSubmit={deleteTemplate}
                        loading={loadingConfirmBtn}/>
            }
        </>
    );
};

export default observer(SectionTemplates);
